/**
 * Member Payments Page
 * 
 * Shows invoices, payment upload, and payment history (receipts)
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import apiClient, { getUploadUrl } from '@/utils/api';
import { formatCurrency } from '@/utils/format';
import { toast } from 'sonner';
import MemberLayout from '@/components/MemberLayout';
import { trackEvent, USAGE_EVENTS } from '@/utils/usageTracker';
import { 
  FileText, 
  Upload, 
  Download, 
  CreditCard, 
  Clock, 
  Eye,
  CheckCircle, 
  AlertCircle,
  Receipt,
  Wallet
} from 'lucide-react';

const MemberPayments = () => {
  const [balance, setBalance] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [notices, setNotices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Payment upload state
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [amount, setAmount] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentFile, setPaymentFile] = useState(null);
  const [uploadingPayment, setUploadingPayment] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  // View pending payment detail + POP
  const [viewPayment, setViewPayment] = useState(null);
  const [popUrl, setPopUrl] = useState(null);
  const [popIsPdf, setPopIsPdf] = useState(false);
  const [popError, setPopError] = useState(false);
  const [loadingPop, setLoadingPop] = useState(false);

  const viewPaymentDetail = async (payment) => {
    setViewPayment(payment);
    setPopUrl(null);
    setPopError(false);
    setPopIsPdf(false);
    if (!payment.pop_attachment?.file_path) return;
    setLoadingPop(true);
    try {
      const res = await apiClient.get(`/member-financial/payments/${payment.payment_id}/pop`, { responseType: 'blob' });
      const blob = res.data;
      setPopIsPdf((blob.type || '').includes('pdf'));
      setPopUrl(window.URL.createObjectURL(blob));
    } catch (e) {
      setPopError(true);
    } finally {
      setLoadingPop(false);
    }
  };

  const closePaymentDetail = () => {
    if (popUrl) window.URL.revokeObjectURL(popUrl);
    setViewPayment(null);
    setPopUrl(null);
    setPopError(false);
  };

  useEffect(() => {
    trackEvent(USAGE_EVENTS.VIEW_PAYMENTS);
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [balanceRes, invoicesRes, receiptsRes, paymentsRes, notifsRes, noticesRes] = await Promise.all([
        apiClient.get('/members/balance'),
        apiClient.get('/members/invoices'),
        apiClient.get('/members/receipts'),
        apiClient.get('/members/payments'),
        apiClient.get('/notifications/my'),
        apiClient.get('/finance/reconciliation-notices/mine').catch(() => ({ data: [] }))
      ]);

      setBalance(balanceRes.data);
      setInvoices(invoicesRes.data);
      setReceipts(receiptsRes.data);
      setPayments(paymentsRes.data);
      setNotifications(notifsRes.data);
      setNotices(noticesRes.data || []);
    } catch (error) {
      toast.error('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  const openPaymentDialog = (invoice = null) => {
    setSelectedInvoice(invoice);
    setAmount(invoice ? invoice.amount.toString() : '');
    setPaymentReference('');
    setPaymentFile(null);
    setPaymentDialogOpen(true);
  };

  const handlePaymentUpload = async (e) => {
    e.preventDefault();
    if (!paymentFile) {
      toast.error('Please select a proof of payment file');
      return;
    }

    setUploadingPayment(true);
    try {
      const formData = new FormData();
      formData.append('amount', amount);
      formData.append('proof_of_payment', paymentFile);
      if (selectedInvoice) {
        formData.append('invoice_id', selectedInvoice.invoice_id);
      }
      if (paymentReference) {
        formData.append('payment_reference', paymentReference);
      }

      await apiClient.post('/payments/upload-pop', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Payment uploaded successfully! Pending verification.');
      trackEvent(USAGE_EVENTS.UPLOAD_PAYMENT_PROOF);
      setPaymentDialogOpen(false);
      setAmount('');
      setPaymentFile(null);
      setSelectedInvoice(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingPayment(false);
    }
  };

  const downloadPDF = async (type, id) => {
    try {
      const response = await apiClient.get(`/pdf/${type}/${id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const downloadReceipt = async (receipt) => {
    try {
      const response = await apiClient.get(`/pdf/receipt/${receipt.payment_id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${receipt.receipt_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      trackEvent(USAGE_EVENTS.DOWNLOAD_RECEIPT, { payment_id: receipt.payment_id });
    } catch (error) {
      toast.error('Failed to download receipt');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-700 border-green-200',
      unpaid: 'bg-amber-100 text-amber-700 border-amber-200',
      pending: 'bg-blue-100 text-blue-700 border-blue-200',
      verified: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
    };
    return (
      <Badge variant="outline" className={styles[status] || ''}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  const currency = balance?.currency || 'USD';
  const outstandingBalance = balance?.outstanding_balance || 0;
  const unpaidInvoices = invoices.filter(inv => inv.status === 'unpaid');
  const paidInvoices = invoices.filter(inv => inv.status === 'paid');
  const unreadNotifications = notifications.filter(n => !n.is_read);

  if (loading) {
    return (
      <MemberLayout currentPage="payments" title="Payments" notificationCount={0}>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </MemberLayout>
    );
  }

  return (
    <MemberLayout currentPage="payments" title="Payments" notificationCount={unreadNotifications.length}>
      {/* Payment Summary Card - Similar to Admin View */}
      {balance && (
        <Card className="mb-6 border-2">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              Payment Summary
            </CardTitle>
            <CardDescription>
              Your membership payment overview
              {balance.total_lives > 1 && (
                <span className="ml-2 text-blue-600 font-medium">
                  ({balance.total_lives} lives: 1 member + {balance.num_beneficiaries} beneficiaries)
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-sm text-blue-700 font-medium">Total Annual Fee</p>
                <p className="text-2xl font-bold text-blue-800">{formatCurrency(balance.total_annual_fee, currency)}</p>
                <p className="text-xs text-blue-600 mt-1">
                  {formatCurrency(balance.monthly_fee, currency)}/month × {balance.membership_period_months} months
                </p>
                {balance.total_lives > 1 && (
                  <p className="text-xs text-blue-500 mt-0.5">
                    ({formatCurrency(balance.base_monthly_fee, currency)} × {balance.total_lives} lives)
                  </p>
                )}
              </div>
              
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="text-sm text-green-700 font-medium">Total Paid</p>
                <p className="text-2xl font-bold text-green-800">{formatCurrency(balance.total_paid, currency)}</p>
                <p className="text-xs text-green-600 mt-1">{receipts.length} payment(s) made</p>
              </div>
              
              <div className={`rounded-lg p-4 border ${outstandingBalance > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                <p className={`text-sm font-medium ${outstandingBalance > 0 ? 'text-red-700' : 'text-green-700'}`}>Outstanding Balance</p>
                <p className={`text-2xl font-bold ${outstandingBalance > 0 ? 'text-red-800' : 'text-green-800'}`}>
                  {formatCurrency(outstandingBalance, currency)}
                </p>
                <p className={`text-xs mt-1 ${outstandingBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {outstandingBalance > 0 ? `${unpaidInvoices.length} invoice(s) pending` : 'All paid up!'}
                </p>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <p className="text-sm text-purple-700 font-medium">Payment Progress</p>
                <p className="text-2xl font-bold text-purple-800">
                  {balance.total_annual_fee > 0 
                    ? Math.round((balance.total_paid / balance.total_annual_fee) * 100)
                    : 0}%
                </p>
                <div className="w-full bg-purple-200 rounded-full h-2 mt-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${Math.min(100, balance.total_annual_fee > 0 ? (balance.total_paid / balance.total_annual_fee) * 100 : 0)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* General Payment Upload */}
      {outstandingBalance > 0 && (
        <Card className="mb-6 border-primary">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold">Make a Payment</h3>
                <p className="text-sm text-muted-foreground">
                  Upload proof of payment to settle your outstanding balance
                </p>
              </div>
              <Button onClick={() => openPaymentDialog(null)} data-testid="upload-general-payment-btn">
                <Upload className="w-4 h-4 mr-2" />
                Upload Payment
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="invoices" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            <FileText className="w-4 h-4 mr-2 hidden sm:block" />
            Invoices ({invoices.length})
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">
            <Clock className="w-4 h-4 mr-2 hidden sm:block" />
            Pending ({payments.filter(p => p.status === 'pending').length})
          </TabsTrigger>
          <TabsTrigger value="receipts" data-testid="tab-receipts">
            <Receipt className="w-4 h-4 mr-2 hidden sm:block" />
            Receipts ({receipts.length})
          </TabsTrigger>
          <TabsTrigger value="notices" data-testid="tab-notices">
            <FileText className="w-4 h-4 mr-2 hidden sm:block" />
            Notices ({notices.length})
          </TabsTrigger>
        </TabsList>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card>
            <CardHeader>
              <CardTitle>Your Invoices</CardTitle>
              <CardDescription>View and pay your invoices</CardDescription>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No invoices found</p>
              ) : (
                <div className="space-y-3">
                  {invoices.map((invoice) => (
                    <div 
                      key={invoice.invoice_id} 
                      className={`flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 border rounded-lg gap-3 ${
                        invoice.status === 'unpaid' ? 'border-amber-200 bg-amber-50/50' : ''
                      }`}
                      data-testid={`invoice-${invoice.invoice_id}`}
                    >
                      <div className="flex-1">
                        <p className="font-semibold">{invoice.invoice_number}</p>
                        <p className="text-sm text-muted-foreground">{invoice.description}</p>
                        {balance?.show_lives_breakdown && (
                          <p className="text-xs text-blue-700 mt-1" data-testid={`invoice-lives-${invoice.invoice_id}`}>
                            {formatCurrency(invoice.per_life_fee ?? balance.base_monthly_fee, currency)} per life ×{' '}
                            {invoice.lives ?? balance.total_lives} live{(invoice.lives ?? balance.total_lives) === 1 ? '' : 's'}{' '}
                            (1 member + {(invoice.lives ?? balance.total_lives) - 1} beneficiar{(invoice.lives ?? balance.total_lives) - 1 === 1 ? 'y' : 'ies'}) ×{' '}
                            {invoice.months_billed ?? 1} month{(invoice.months_billed ?? 1) === 1 ? '' : 's'}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Due: {new Date(invoice.due_date).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
                        <div className="text-left sm:text-right">
                          <p className="font-bold">{formatCurrency(invoice.amount, currency)}</p>
                          {getStatusBadge(invoice.status)}
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => downloadPDF('invoices', invoice.invoice_id)}
                            data-testid={`download-invoice-${invoice.invoice_id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          {invoice.status === 'unpaid' && (
                            <Button 
                              size="sm" 
                              onClick={() => openPaymentDialog(invoice)}
                              data-testid={`pay-invoice-${invoice.invoice_id}`}
                            >
                              <CreditCard className="w-4 h-4 mr-1" />
                              Pay
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Payments Tab */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Pending Payments</CardTitle>
              <CardDescription>Payments awaiting verification</CardDescription>
            </CardHeader>
            <CardContent>
              {payments.filter(p => p.status === 'pending').length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No pending payments</p>
              ) : (
                <div className="space-y-3">
                  {payments.filter(p => p.status === 'pending').map((payment) => (
                    <div 
                      key={payment.payment_id} 
                      className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 border rounded-lg gap-3"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Clock className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold">Payment #{payment.payment_id?.slice(-8)}</p>
                          <p className="text-sm text-muted-foreground">
                            Ref: {payment.payment_reference || 'N/A'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Submitted: {new Date(payment.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-left sm:text-right flex flex-col items-start sm:items-end gap-2">
                        <p className="font-bold">{formatCurrency(payment.amount, currency)}</p>
                        {getStatusBadge(payment.status)}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewPaymentDetail(payment)}
                          data-testid={`view-payment-${payment.payment_id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Receipts Tab */}
        <TabsContent value="receipts">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Payment Receipts
              </CardTitle>
              <CardDescription>Download receipts for your verified payments</CardDescription>
            </CardHeader>
            <CardContent>
              {receipts.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No receipts available</p>
              ) : (
                <div className="space-y-3">
                  {receipts.map((receipt) => (
                    <div 
                      key={receipt.receipt_id} 
                      className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 border rounded-lg gap-3"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Receipt className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-semibold">{receipt.receipt_number}</p>
                          <p className="text-sm text-muted-foreground">{receipt.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(receipt.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="font-bold">{formatCurrency(receipt.amount, currency)}</p>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => downloadReceipt(receipt)}
                          data-testid={`download-receipt-${receipt.receipt_id}`}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reconciliation Notices Tab */}
        <TabsContent value="notices">
          <Card>
            <CardHeader>
              <CardTitle>Payment Reconciliation Notices</CardTitle>
              <CardDescription>Official notices that supersede a previously issued receipt</CardDescription>
            </CardHeader>
            <CardContent>
              {notices.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No reconciliation notices</p>
              ) : (
                <div className="space-y-3">
                  {notices.map((n) => (
                    <div key={n.notice_id} className="flex items-center justify-between border rounded-lg p-4 border-amber-200 bg-amber-50/40" data-testid={`notice-${n.notice_id}`}>
                      <div className="min-w-0">
                        <p className="font-semibold text-amber-900">{n.reconciliation_number}</p>
                        <p className="text-sm text-muted-foreground">
                          Reverses receipt {n.original_receipt_number} · {n.reason}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {n.reconciled_date ? new Date(n.reconciled_date).toLocaleDateString() : ''} · Amount reversed: {formatCurrency(n.amount, currency)}
                        </p>
                      </div>
                      <a
                        href={getUploadUrl(`gridfs:${n.pdf_file_id}`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`download-notice-${n.notice_id}`}
                      >
                        <Button size="sm" variant="outline">
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Pending Payment Detail (read-only, with POP preview) */}
      <Dialog open={!!viewPayment} onOpenChange={(o) => { if (!o) closePaymentDetail(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="payment-detail-modal">
          <DialogHeader>
            <DialogTitle>Payment Details</DialogTitle>
            <DialogDescription>Awaiting verification — read only</DialogDescription>
          </DialogHeader>
          {viewPayment && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Amount</p>
                  <p className="font-semibold" data-testid="detail-amount">{formatCurrency(viewPayment.amount, currency)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <div>{getStatusBadge(viewPayment.status)}</div>
                </div>
                <div>
                  <p className="text-muted-foreground">Submitted</p>
                  <p className="font-medium">{new Date(viewPayment.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Method</p>
                  <p className="font-medium">{(viewPayment.payment_method || 'N/A').replace('_', ' ')}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-muted-foreground">Reference</p>
                  <p className="font-medium">{viewPayment.payment_reference || 'N/A'}</p>
                </div>
              </div>

              <div className="border-t pt-3">
                <p className="text-sm font-medium mb-2">Proof of Payment</p>
                {loadingPop ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
                ) : popError || (!popUrl && viewPayment.pop_attachment) ? (
                  <div className="text-center py-6 border rounded-lg bg-muted/40" data-testid="pop-fallback">
                    <p className="text-sm text-muted-foreground">Proof of payment could not be loaded.</p>
                  </div>
                ) : !viewPayment.pop_attachment ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No proof of payment was attached.</p>
                ) : popIsPdf ? (
                  <div className="text-center py-6 border rounded-lg" data-testid="pop-pdf">
                    <p className="text-sm text-muted-foreground mb-2">PDF document</p>
                    <a href={popUrl} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="outline"><Eye className="w-4 h-4 mr-1" /> Open PDF</Button>
                    </a>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="pop-image">
                    <img src={popUrl} alt="Proof of payment" className="max-h-72 w-full object-contain rounded-lg border bg-white" />
                    <a href={popUrl} target="_blank" rel="noopener noreferrer" className="inline-block">
                      <Button size="sm" variant="outline"><Download className="w-4 h-4 mr-1" /> Download / Expand</Button>
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closePaymentDetail} data-testid="close-payment-detail">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Payment Upload Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedInvoice ? `Pay Invoice: ${selectedInvoice.invoice_number}` : 'Upload Payment'}
            </DialogTitle>
            <DialogDescription>
              Upload proof of payment for verification
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePaymentUpload} className="space-y-4">
            <div>
              <Label htmlFor="amount">Amount ({currency})</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                data-testid="payment-amount-input"
              />
            </div>
            <div>
              <Label htmlFor="reference">Payment Reference (Optional)</Label>
              <Input
                id="reference"
                placeholder="Bank reference or transaction ID"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                data-testid="payment-reference-input"
              />
            </div>
            <div>
              <Label htmlFor="file">Proof of Payment *</Label>
              <Input
                id="file"
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setPaymentFile(e.target.files[0])}
                required
                data-testid="payment-file-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Upload bank receipt, screenshot, or PDF (max 5MB)
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploadingPayment} data-testid="submit-payment-btn">
                {uploadingPayment ? 'Uploading...' : 'Submit Payment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </MemberLayout>
  );
};

export default MemberPayments;
